import React, { useState, useEffect } from "react";
import {
  Edit2,
  Trash2,
  Search,
  ChevronLeft,
  ChevronRight,
  RefreshCw,
} from "lucide-react";
import "./VenderMaster.css"; 
import axios from "axios";
import { toast } from "react-toastify";

const VenderMaster = () => {
  const initialFormState = {
    vendorCode: "",
    vendorName: "",
    vendorAddress: "",
    mobileNo: "",
    email: "",
    gstNo: "",
    accountNo: "",
    bankName: "",
    ifscCode: "",
  };

  const [formData, setFormData] = useState(initialFormState);
  const [vendorMasters, setVendorMasters] = useState([]); // Standardized spelling
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [totalRecords, setTotalRecords] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [isEditing, setIsEditing] = useState(false);

  const API_URL = "https://payyug.in/api"; 
  const superAdminId = localStorage.getItem("adminuserId");

  // 1. FETCH DATA
  const fetchVendorMaster = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/vendor/master/list`, {
        params: {
          superAdminId,
          page: currentPage, 
          limit: itemsPerPage,
        },
      });
      if (res.data.success) {
        setVendorMasters(res.data.data || []); 
        setTotalPages(res.data.totalPages || 1);
        setTotalRecords(res.data.totalRecords || 0);
      }
    } catch (err) {
      console.error("Fetch Error:", err);
      toast.error("Failed to load records");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchVendorMaster();
  }, [currentPage, itemsPerPage, superAdminId]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  // 2. CREATE & UPDATE
  const handleSubmit = async (e) => {
    e.preventDefault();
    const payload = { ...formData, superAdminId };
    try {
      if (isEditing) {
        const res = await axios.put(`${API_URL}/vendor/master/${formData.id}`, payload);
        if (res.data.success) toast.success(res.data.message||"Vendor Updated Successfully!");
      } else {
        const res = await axios.post(`${API_URL}/vendor/master/create`, payload);
        if (res.data.success) toast.success(res.data.message||"New Vendor Created!");
      }
      handleReset();
      fetchVendorMaster();
    } catch (err) {
      toast.error(err.response?.data?.message || "Operation failed");
    }
  };

  // 3. DELETE
  const handleDelete = async (id) => {
    if (window.confirm("Are you sure you want to delete this vendor?")) {
      try {
        const res = await axios.delete(`${API_URL}/vendor/master/${id}`);
        if (res.data.success) {
          toast.success(res.data.message||"Vendor Deleted!");
          fetchVendorMaster();
        }
      } catch (err) {
        toast.error("Delete failed");
      }
    }
  };

  const handleReset = () => {
    setFormData(initialFormState);
    setIsEditing(false);
  };

  // FIXED SEARCH LOGIC
  const filteredItems = vendorMasters.filter((v) =>
    (v.vendorName || "").toLowerCase().includes(searchTerm.toLowerCase()) ||
    (v.vendorCode || "").toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleNext = () => { if (currentPage < totalPages) setCurrentPage(prev => prev + 1); };
  const handlePrev = () => { if (currentPage > 1) setCurrentPage(prev => prev - 1); };

  const handleLimitChange = (e) => {
    setItemsPerPage(Number(e.target.value));
    setCurrentPage(1);
  };

  return (
    <div className="vender-master-container">
      <div className="vender-master-header-card">
        <h2>{isEditing ? "Edit Vendor" : "Create New Vendor"}</h2>
        <button className="reset-btn" onClick={handleReset}>
          <RefreshCw size={14} /> Reset
        </button>
      </div>

      <div className="vender-master-form-card">
        <form onSubmit={handleSubmit}>
          <div className="vender-master-form-grid">
            {/* Map through fields to keep JSX clean */}
            {[
              { label: "Vendor Code", name: "vendorCode",type:"text" },
              { label: "Vendor Name", name: "vendorName",type:"text" },
              { label: "Vendor Address", name: "vendorAddress",type:"text" },
              { label: "Mobile No", name: "mobileNo",type:"number" },
              { label: "Email", name: "email", type: "email" },
              { label: "GST No", name: "gstNo",type:"text" },
              { label: "Account No", name: "accountNo",type:"text" },
              { label: "Bank Name", name: "bankName",type:"text" },
              { label: "IFSC Code", name: "ifscCode",type:"text" },
            ].map((field) => (
              <div key={field.name} className="vender-master-form-input-group">
                <label>{field.label}</label>
                <input
                  type={field.type || "text"}
                  name={field.name}
                  value={formData[field.name]}
                  onChange={handleChange}
                  required
                />
              </div>
            ))}
          </div>

          <div style={{ marginTop: "20px", display: "flex", justifyContent: "flex-end" }}>
            <button type="submit" className="vender-master-form-isubmin-btn">
              {isEditing ? "Update" : "Save"}
            </button>
          </div>
        </form>
      </div>

      <div className="vender-master-table-container">
        <div className="vender-master-table-controles">
          <div>
            Show{" "}
            <select value={itemsPerPage} onChange={handleLimitChange}>
              <option value={10}>10</option>
              <option value={25}>25</option>
              <option value={50}>50</option>
            </select> entries
          </div>
          <div className="vender-master-table-search-box" style={{ position: "relative" }}>
            <Search size={16} style={{ position: "absolute", left: "10px", top: "10px", color: "#999" }} />
            <input
              placeholder="Search by name or code..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{ paddingLeft: "35px" }}
            />
          </div>
        </div>

        <div className="vender-master-report-container">
          <table className="vender-master-table-report">
            <thead>
              <tr>
                <th>S.No.</th>
                <th>Code</th>
                <th>Name</th>
                <th>Address</th>
                <th>Mobile</th>
                <th>Email</th>
                <th>GST</th>
                <th>Account</th>
                <th>Bank</th>
                <th>IFSC</th>
                <th style={{ textAlign: "center" }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan="11" style={{ textAlign: "center" }}>Loading Records...</td></tr>
              ) : filteredItems.length > 0 ? (
                filteredItems.map((v, index) => (
                  <tr key={v.id || index}>
                    <td>{(currentPage - 1) * itemsPerPage + index + 1}</td>
                    <td>{v.vendorCode || "N/A"}</td>
                    <td style={{ fontWeight: "600" }}>{v.vendorName || "N/A"}</td>
                    <td>{v.vendorAddress || "N/A"}</td>
                    <td>{v.mobileNo || "N/A"}</td>
                    <td>{v.email || "N/A"}</td>
                    <td>{v.gstNo || "N/A"}</td>
                    <td>{v.accountNo || "N/A"}</td>
                    <td>{v.bankName || "N/A"}</td>
                    <td>{v.ifscCode || "N/A"}</td>
                    <td style={{ textAlign: "center" }}>
                      <button className="btn-edit" onClick={() => { setFormData(v); setIsEditing(true); }}>
                        <Edit2 size={16} />
                      </button>
                      <button className="btn-delete" onClick={() => handleDelete(v.id)}>
                        <Trash2 size={16} />
                      </button>
                    </td>
                  </tr>
                ))
              ) : (
                <tr><td colSpan="11" style={{ textAlign: "center" }}>No records found</td></tr>
              )}
            </tbody>
          </table>
        </div>

        <div className="vender-master-pagination">
          <div className="pagination-info">
            Page <strong>{currentPage}</strong> of {totalPages} ({totalRecords} items)
          </div>
          <div className="pagination-buttons">
            <button 
              className="product-loan-scheme-pagination-btn" 
              disabled={currentPage === 1 || loading} 
              onClick={handlePrev}
            >
              <ChevronLeft size={14} /> Previous
            </button>
            <button 
              className="product-loan-scheme-pagination-btn" 
              disabled={currentPage >= totalPages || loading} 
              onClick={handleNext}
            >
              Next <ChevronRight size={14} />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VenderMaster;